/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { loadPayPalSdk } from '@/lib/paypal/load-sdk';
import type { PayPalFundingSource, PayPalNamespace, PayPalSdkConfig } from '@/lib/paypal/types';

/** Normalized order detail returned by the get-order server action. */
export interface PayPalApprovedOrder {
    id: string;
    fundingSource: PayPalFundingSource;
    status?: string;
    zipMatch?: boolean;
    payer?: { email: string; firstName: string; lastName: string; phone: string };
    shippingAddress?: Record<string, string> | null;
}

export interface PayPalButtonProps {
    /** Public SDK config (from config.payment.paypal). */
    config: PayPalSdkConfig;
    /**
     * Placement variant. `cart` = express (PayPal supplies the shipping address);
     * `checkout` = payment step (validated checkout address is passed to PayPal).
     */
    variant: 'cart' | 'checkout';
    /** Whether to render the Pay Later (Pay in 4) button below PayPal. */
    showPayLater?: boolean;
    /**
     * Button arrangement. `row` (default) = a single wrapping row of equal-width buttons
     * (checkout). `stack` = a full-width vertical stack (cart express, AshCom CART-16 design).
     */
    layout?: 'row' | 'stack';
    /**
     * Optional node rendered between the PayPal and Pay Later buttons — used by the cart express
     * section to mount the PayPal Pay Later financing message in the AshCom layout.
     */
    payLaterSlot?: ReactNode;
    /** Cart express: the shopper's persisted ZIP, used to validate the PayPal-returned ZIP. */
    expectedZip?: string;
    /** Called after a successful approve + read-back. */
    onApproved?: (order: PayPalApprovedOrder) => void;
    /** Called when the PayPal ZIP does not match the persisted ZIP (cart express). */
    onZipMismatch?: (order: PayPalApprovedOrder) => void;
    /** Called on cancel. */
    onCancel?: () => void;
    /** Called on any error (SDK load, create, approve). */
    onError?: (message: string) => void;
    className?: string;
}

/** Posts form-encoded data to a same-origin server action and returns parsed JSON. */
async function postAction<T>(action: string, fields: Record<string, string>): Promise<T> {
    const body = new FormData();
    Object.entries(fields).forEach(([key, value]) => body.set(key, value));
    const response = await fetch(action, { method: 'POST', body });
    return (await response.json()) as T;
}

/**
 * Funding-button height/shape from the AshCom "Express Buttons" spec (Figma 12941:22050):
 * each button is 56px tall with a 4px radius. The PayPal SDK clamps `style.height` to a max of
 * 55px, so we pin to 55 (visually identical) and reuse the same value for the placeholder + the
 * reserved min-height so the gold button, Pay Later button, and Venmo slot all line up. Without an
 * explicit height the SDK uses responsive sizing that scales the button with its container width,
 * which is why the buttons rendered oversized.
 */
const BUTTON_HEIGHT = 55;
const BUTTON_BORDER_RADIUS = 4;

/**
 * The PayPal JS SDK reports a shopper-closed popup through `onError` (not `onCancel`) with messages
 * like "Detected popup close" / "Window is closed, can not determine type". These are benign
 * cancellations — the shopper backed out — and must not surface as a red error banner.
 */
function isPopupClosedError(message: string): boolean {
    const normalized = message.toLowerCase();
    return (
        normalized.includes('detected popup close') ||
        normalized.includes('window is closed') ||
        normalized.includes('popup close')
    );
}

/**
 * PayPal Commerce Platform button stack (INT-24).
 *
 * Renders the PayPal button and (optionally) the Pay Later button via the PayPal JS SDK, plus
 * an optional disabled Venmo placeholder slot. SDK callbacks proxy through the SFNext server
 * actions (`/action/paypal-create-order`, `/action/paypal-get-order`) — the browser never
 * calls PayPal or SCAPI directly.
 *
 * Placement into the cart / checkout pages is owned by CART-16 / CHECKOUT-02; this component is
 * the reusable integration surface.
 */
export default function PayPalButton({
    config,
    variant,
    showPayLater = true,
    layout = 'row',
    payLaterSlot,
    expectedZip,
    onApproved,
    onZipMismatch,
    onCancel,
    onError,
    className,
}: PayPalButtonProps) {
    const paypalRef = useRef<HTMLDivElement | null>(null);
    const payLaterRef = useRef<HTMLDivElement | null>(null);
    const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

    const createOrder = useCallback(async (): Promise<string> => {
        const result = await postAction<{ success: boolean; id?: string; error?: string }>(
            '/action/paypal-create-order',
            { isExpressCheckout: String(variant === 'cart') }
        );
        if (!result.success || !result.id) {
            throw new Error(result.error ?? 'Unable to create PayPal order');
        }
        return result.id;
    }, [variant]);

    const onApprove = useCallback(
        (fundingSource: PayPalFundingSource) =>
            async (data: { orderID: string }): Promise<void> => {
                const fields: Record<string, string> = { orderId: data.orderID };
                if (variant === 'cart' && expectedZip) {
                    fields.expectedZip = expectedZip;
                }
                const order = await postAction<
                    { success: boolean } & Omit<PayPalApprovedOrder, 'fundingSource'> & { error?: string }
                >('/action/paypal-get-order', fields);
                if (!order.success) {
                    onError?.(order.error ?? 'Unable to read PayPal order');
                    return;
                }
                const approvedOrder: PayPalApprovedOrder = { ...order, fundingSource };
                // Ashley pricing/inventory is ZIP-driven — a mismatch must route to address entry.
                if (variant === 'cart' && approvedOrder.zipMatch === false) {
                    onZipMismatch?.(approvedOrder);
                    return;
                }
                onApproved?.(approvedOrder);
            },
        [variant, expectedZip, onApproved, onZipMismatch, onError]
    );

    useEffect(() => {
        let cancelled = false;
        setStatus('loading');

        loadPayPalSdk(config)
            .then((paypal: PayPalNamespace) => {
                if (cancelled) {
                    return;
                }

                const shared = {
                    // Pin the funding-button size to the AshCom spec instead of the SDK's
                    // width-responsive default (which renders an oversized button).
                    style: { height: BUTTON_HEIGHT, borderRadius: BUTTON_BORDER_RADIUS },
                    createOrder,
                    onApprove,
                    onCancel: () => onCancel?.(),
                    onError: (err: unknown) => {
                        const message = err instanceof Error ? err.message : 'PayPal error';
                        // A shopper-closed popup is a cancel, not a failure — don't show an error.
                        if (isPopupClosedError(message)) {
                            onCancel?.();
                            return;
                        }
                        onError?.(message);
                    },
                };

                if (paypalRef.current) {
                    paypalRef.current.innerHTML = '';
                    const ppButton = paypal.Buttons({
                        fundingSource: paypal.FUNDING.PAYPAL,
                        ...shared,
                        onApprove: onApprove('PAYPAL'),
                    });
                    if (ppButton.isEligible()) {
                        void ppButton.render(paypalRef.current);
                    }
                }

                if (showPayLater && payLaterRef.current && paypal.FUNDING.PAYLATER) {
                    payLaterRef.current.innerHTML = '';
                    const plButton = paypal.Buttons({
                        fundingSource: paypal.FUNDING.PAYLATER,
                        ...shared,
                        onApprove: onApprove('PAYLATER'),
                    });
                    const payLaterEligible = plButton.isEligible();
                    if (payLaterEligible) {
                        void plButton.render(payLaterRef.current);
                    }
                }

                setStatus('ready');
            })
            .catch((err: unknown) => {
                if (cancelled) {
                    return;
                }
                setStatus('error');
                onError?.(err instanceof Error ? err.message : 'Failed to load PayPal');
            });

        return () => {
            cancelled = true;
        };
        // createOrder/onApprove are stable via useCallback; config is referentially stable per render tree.
    }, [config, createOrder, onApprove, onCancel, onError, showPayLater]);

    // PayPal outage: render nothing actionable (graceful degradation — shopper uses another tender).
    if (status === 'error') {
        return (
            <div className={cn('text-sm text-muted-foreground', className)} role="status">
                PayPal is temporarily unavailable. Please choose another payment method.
            </div>
        );
    }

    const isStack = layout === 'stack';
    // `row` (checkout): a single wrapping row of equal-width buttons (PayPal | Pay Later | Venmo);
    // `flex-1` splits the row into equal columns and `min-w-[150px]` (PayPal's minimum button width)
    // lets them wrap on narrow viewports. `stack` (cart express, AshCom CART-16): a full-width
    // vertical stack so each funding button spans the order-summary rail.
    const wrapperClass = isStack ? 'flex flex-col gap-2' : 'flex flex-row flex-wrap gap-2';
    const buttonClass = isStack ? 'w-full' : 'flex-1 min-w-[150px]';

    return (
        <div className={cn(wrapperClass, className)} data-testid="paypal-button-stack">
            {/* Reserve height to avoid layout shift while the SDK renders the button. */}
            <div
                ref={paypalRef}
                data-testid="paypal-button"
                className={buttonClass}
                style={{ minHeight: BUTTON_HEIGHT }}
            />
            {showPayLater && payLaterSlot}
            {showPayLater && (
                <div
                    ref={payLaterRef}
                    data-testid="paypal-paylater-button"
                    className={buttonClass}
                    style={{ minHeight: BUTTON_HEIGHT }}
                />
            )}
        </div>
    );
}
